/**
 * /payments/:paymentId — payment detail view.
 * Shows all PaymentOut fields, applied invoices, and action buttons.
 * CHECK-method payments get a "Download Check PDF" button.
 * Any payment can be deleted (void) by users with appropriate role.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  apInvoicesListQueryOptions,
  arInvoicesListQueryOptions,
  paymentDetailQueryOptions,
  paymentKeys,
  periodsListQueryOptions,
} from "@/lib/queries";
import { api, getToken } from "@/lib/api";
import type {
  APInvoice,
  ARInvoice,
  FiscalPeriod,
  Payment,
  PaymentApplication,
} from "@/lib/types";
import { PaymentAttachmentsSection } from "@/components/PaymentAttachmentsSection";
import { MarkIssuedDialog } from "@/components/MarkIssuedDialog";

export const Route = createFileRoute("/_auth/payments/$paymentId")({
  component: PaymentDetailPage,
});

const METHOD_LABEL: Record<string, string> = {
  check: "Check",
  ach: "ACH",
  wire: "Wire",
  cash: "Cash",
  credit_card: "Credit Card",
  debit_card: "Debit Card",
  zelle: "Zelle",
  venmo: "Venmo",
  paypal: "PayPal",
  crypto: "Crypto",
  other: "Other",
};

function fmt(n: string | number, currency = "CAD") {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      });
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
        {label}
      </dt>
      <dd className="text-sm text-slate-800 dark:text-slate-200 font-medium">
        {value ?? <span className="text-slate-400 dark:text-slate-500">—</span>}
      </dd>
    </div>
  );
}

async function openCheckPdf(paymentId: string) {
  const token = getToken();
  const res = await fetch(`/api/payments/${paymentId}/check.pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    alert(`Could not generate check PDF: ${body.detail ?? res.statusText}`);
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

function ApplicationsTable({ apps, currency }: { apps: PaymentApplication[]; currency: string }) {
  const navigate = useNavigate();
  if (apps.length === 0)
    return (
      <p className="text-sm text-slate-400 dark:text-slate-500">No applications recorded.</p>
    );

  return (
    <div className="rounded-lg border border-slate-200 dark:border-gray-800 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          <tr>
            <th className="text-left px-3 py-2.5">AP Invoice ID</th>
            <th className="text-left px-3 py-2.5">AR Invoice ID</th>
            <th className="text-right px-3 py-2.5">Applied</th>
            <th className="text-right px-3 py-2.5">Discount</th>
            <th className="text-right px-3 py-2.5">Retention Release</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
          {apps.map((app) => (
            <tr key={app.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50">
              <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                {app.ap_invoice_id ? (
                  <button
                    type="button"
                    className="font-medium text-blue-700 hover:underline"
                    onClick={() => navigate({
                      to: "/invoices/ap/$invoiceId",
                      params: { invoiceId: app.ap_invoice_id! },
                    })}
                  >
                    Open AP invoice
                  </button>
                ) : "—"}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
                {app.ar_invoice_id ? (
                  <button
                    type="button"
                    className="font-medium text-blue-700 hover:underline"
                    onClick={() => navigate({
                      to: "/invoices/ar/$invoiceId",
                      params: { invoiceId: app.ar_invoice_id! },
                    })}
                  >
                    Open AR invoice
                  </button>
                ) : "—"}
              </td>
              <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900 dark:text-slate-100">
                {fmt(app.applied_amount, currency)}
              </td>
              <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">
                {parseFloat(app.discount_amount) > 0
                  ? fmt(app.discount_amount, currency)
                  : "—"}
              </td>
              <td className="px-3 py-2 text-right font-mono text-slate-600 dark:text-slate-400">
                {parseFloat(app.retention_release_amount) > 0
                  ? fmt(app.retention_release_amount, currency)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentDetailPage() {
  const { paymentId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [markIssuedOpen, setMarkIssuedOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidDate, setVoidDate] = useState(new Date().toISOString().slice(0, 10));
  const [voidPeriodId, setVoidPeriodId] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyInvoiceId, setApplyInvoiceId] = useState("");
  const [applyAmount, setApplyAmount] = useState("");
  const [applyDate, setApplyDate] = useState(new Date().toISOString().slice(0, 10));
  const [applyPeriodId, setApplyPeriodId] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const pmtQ = useQuery(paymentDetailQueryOptions(paymentId));
  const periodsQ = useQuery(periodsListQueryOptions());
  const pmt: Payment | undefined = pmtQ.data;
  const apInvoicesQ = useQuery({
    ...apInvoicesListQueryOptions(
      pmt?.direction === "outgoing" ? { vendor_id: pmt.party_id } : {},
    ),
    enabled: applyOpen && pmt?.direction === "outgoing",
  });
  const arInvoicesQ = useQuery({
    ...arInvoicesListQueryOptions(
      pmt?.direction === "incoming" ? { customer_id: pmt.party_id } : {},
    ),
    enabled: applyOpen && pmt?.direction === "incoming",
  });
  const openPeriods = useMemo(
    () => (periodsQ.data ?? []).filter((period: FiscalPeriod) => period.status === "open"),
    [periodsQ.data],
  );
  const applicableInvoices = useMemo(() => {
    if (!pmt) return [];
    const invoices = pmt.direction === "outgoing"
      ? (apInvoicesQ.data ?? [])
      : (arInvoicesQ.data ?? []);
    return invoices.filter((invoice) =>
      (invoice.status === "posted" || invoice.status === "partial_paid") &&
      invoice.currency_code === pmt.currency_code,
    );
  }, [pmt, apInvoicesQ.data, arInvoicesQ.data]);

  async function handleDelete() {
    if (!pmt) return;
    if (
      !confirm(
        `Delete unissued payment ${pmt.payment_no}?`,
      )
    )
      return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await api(`/api/payments/${pmt.id}`, { method: "DELETE" });
      // Invalidate list and navigate back
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      navigate({ to: "/payments" });
    } catch (err: unknown) {
      const msg =
        (err as { data?: { detail?: string }; message?: string })?.data?.detail ??
        (err as Error)?.message ??
        "Delete failed";
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  }

  function beginVoid() {
    const today = new Date().toISOString().slice(0, 10);
    const matching = openPeriods.find(
      (period) => today >= period.start_date && today <= period.end_date,
    );
    setVoidDate(today);
    setVoidPeriodId(matching?.id ?? openPeriods[0]?.id ?? "");
    setVoidReason("");
    setVoidError(null);
    setVoidOpen(true);
  }

  async function handleVoid() {
    if (!pmt || !voidPeriodId || !voidReason.trim()) return;
    setVoiding(true);
    setVoidError(null);
    try {
      await api(`/api/payments/${pmt.id}/void`, {
        method: "POST",
        body: {
          reversal_date: voidDate,
          period_id: voidPeriodId,
          reason: voidReason.trim(),
        },
      });
      await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      setVoidOpen(false);
    } catch (err: unknown) {
      setVoidError((err as Error)?.message ?? "Void failed");
    } finally {
      setVoiding(false);
    }
  }

  function beginApply() {
    if (!pmt) return;
    const today = new Date().toISOString().slice(0, 10);
    const matching = openPeriods.find(
      (period) => today >= period.start_date && today <= period.end_date,
    );
    setApplyDate(today);
    setApplyPeriodId(matching?.id ?? openPeriods[0]?.id ?? "");
    setApplyInvoiceId("");
    setApplyAmount(pmt.unapplied_amount);
    setApplyError(null);
    setApplyOpen(true);
  }

  function selectApplyInvoice(invoiceId: string) {
    setApplyInvoiceId(invoiceId);
    const invoice = applicableInvoices.find((item) => item.id === invoiceId);
    if (!invoice || !pmt) return;
    const outstanding = "amount_due" in invoice
      ? Number((invoice as APInvoice).amount_due)
      : Number((invoice as ARInvoice).total) - Number((invoice as ARInvoice).received_amount);
    setApplyAmount(String(Math.min(Number(pmt.unapplied_amount), outstanding).toFixed(2)));
  }

  async function handleApply() {
    if (!pmt || !applyInvoiceId || !applyPeriodId || Number(applyAmount) <= 0) return;
    setApplying(true);
    setApplyError(null);
    try {
      await api(`/api/payments/${pmt.id}/applications`, {
        method: "POST",
        body: {
          application_date: applyDate,
          period_id: applyPeriodId,
          applications: [{
            [pmt.direction === "outgoing" ? "ap_invoice_id" : "ar_invoice_id"]: applyInvoiceId,
            applied_amount: Number(applyAmount),
            discount_amount: 0,
            retention_release_amount: 0,
          }],
        },
      });
      await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      await queryClient.invalidateQueries({
        queryKey: [pmt.direction === "outgoing" ? "ap-invoices" : "ar-invoices"],
      });
      await queryClient.invalidateQueries({ queryKey: ["gl"] });
      setApplyOpen(false);
    } catch (err: unknown) {
      setApplyError((err as Error)?.message ?? "Application failed");
    } finally {
      setApplying(false);
    }
  }

  if (pmtQ.isLoading)
    return (
      <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</div>
    );

  if (pmtQ.error)
    return (
      <div className="p-6 text-sm text-red-600 space-y-3">
        <p>Error: {(pmtQ.error as Error).message}</p>
        <div className="flex gap-2">
          <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={() => pmtQ.refetch()}>
            Retry
          </button>
          <button className="rounded border border-slate-300 px-3 py-2 text-slate-700" onClick={() => navigate({ to: "/payments" })}>
            Back to payments
          </button>
        </div>
      </div>
    );

  if (!pmt)
    return (
      <div className="p-6 text-sm text-slate-500 dark:text-slate-400 space-y-3">
        <p>Payment not found.</p>
        <button className="rounded bg-blue-600 px-3 py-2 text-white" onClick={() => navigate({ to: "/payments" })}>
          Back to payments
        </button>
      </div>
    );

  const isOutgoing = pmt.direction === "outgoing";
  const isCheck = pmt.method === "check";
  const isIssued = pmt.status === "issued";
  const isPending = pmt.status === "pending" || pmt.status === "draft";
  const isVoided = pmt.status === "voided";

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate({ to: "/payments" })}
            className="text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            ← Payments
          </button>
          <span className="text-slate-300 dark:text-slate-700">/</span>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {pmt.payment_no}
          </h1>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              isOutgoing
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            }`}
          >
            {isOutgoing ? "AP" : "AR"}
          </span>
          <div className="flex-1" />

          {isCheck && !isVoided && (
            <button
              onClick={() => openCheckPdf(pmt.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors"
            >
              Download Check PDF
            </button>
          )}

          {!isCheck && isPending && (
            <button
              onClick={() => setMarkIssuedOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
            >
              {isOutgoing ? "Confirm transfer" : "Confirm receipt"}
            </button>
          )}
          {isIssued && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Issued {pmt.issued_at?.slice(0, 10) ?? ""}
            </span>
          )}
          {isIssued && Number(pmt.unapplied_amount) > 0 && (
            <button
              onClick={beginApply}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Apply unapplied
            </button>
          )}

          {isIssued && (
            <button
              onClick={beginVoid}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 text-sm font-medium transition-colors"
            >
              Void Payment
            </button>
          )}
          {isPending && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete Draft"}
            </button>
          )}
        </div>
        {deleteError && (
          <p className="mt-1 text-xs text-red-600">{deleteError}</p>
        )}
      </div>

      <div className="px-6 py-6 space-y-8 max-w-5xl">
        {/* Payment header fields */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-4">
            Payment Details
          </h2>
          <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
            <Field label="Payment #" value={<span className="font-mono">{pmt.payment_no}</span>} />
            <Field label="Date" value={pmt.payment_date} />
            <Field label="Direction" value={isOutgoing ? "Outgoing (AP)" : "Incoming (AR)"} />
            <Field
              label="Method"
              value={
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    isCheck
                      ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                      : "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-400"
                  }`}
                >
                  {METHOD_LABEL[pmt.method] ?? pmt.method}
                </span>
              }
            />
            <Field label="Amount" value={<span className="font-mono">{fmt(pmt.amount, pmt.currency_code)}</span>} />
            <Field label="Status" value={pmt.status.replace("_", " ")} />
            <Field label="Unapplied" value={<span className="font-mono">{fmt(pmt.unapplied_amount, pmt.currency_code)}</span>} />
            <Field label="Currency" value={pmt.currency_code} />
            <Field label="FX Rate" value={pmt.fx_rate !== "1" && pmt.fx_rate !== "1.00" ? pmt.fx_rate : "1.00 (domestic)"} />
            {isCheck && <Field label="Check #" value={pmt.check_no ? <span className="font-mono">{pmt.check_no}</span> : null} />}
            <Field label="Reference" value={pmt.reference ? <span className="font-mono">{pmt.reference}</span> : null} />
            <Field label="Memo" value={pmt.memo} />
            <Field label="Party ID" value={<span className="font-mono text-xs">{pmt.party_id}</span>} />
            <Field label="Period ID" value={<span className="font-mono text-xs">{pmt.period_id}</span>} />
            <Field label="Bank Account ID" value={<span className="font-mono text-xs">{pmt.bank_account_id}</span>} />
            {pmt.journal_id && (
              <Field
                label="Journal ID"
                value={<span className="font-mono text-xs">{pmt.journal_id}</span>}
              />
            )}
          </dl>
        </section>

        {/* Applied invoices */}
        <section>
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-4">
            Applied Invoices ({pmt.applications.length})
          </h2>
          <ApplicationsTable apps={pmt.applications} currency={pmt.currency_code} />
        </section>

        {/* Manual-payment metadata (only meaningful for manual methods) */}
        {(pmt.external_reference || pmt.issued_at) && (
          <section>
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-4">
              Confirmation
            </h2>
            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-4">
              <Field
                label="External Reference"
                value={
                  pmt.external_reference ? (
                    <span className="font-mono text-xs">
                      {pmt.external_reference}
                    </span>
                  ) : null
                }
              />
              <Field label="Issued At" value={pmt.issued_at ?? null} />
            </dl>
          </section>
        )}

        {/* Attachments — proof-of-payment screenshots, wire receipts, etc. */}
        <PaymentAttachmentsSection paymentId={pmt.id} />
      </div>

      <MarkIssuedDialog
        payment={pmt}
        open={markIssuedOpen}
        onClose={() => setMarkIssuedOpen(false)}
      />
      {applyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-900">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Apply {fmt(pmt.unapplied_amount, pmt.currency_code)}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Reclassify unapplied cash to an open {isOutgoing ? "vendor bill" : "customer invoice"}.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-600">Invoice</label>
            <select
              value={applyInvoiceId}
              onChange={(event) => selectApplyInvoice(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <option value="">Select an open invoice</option>
              {applicableInvoices.map((invoice) => (
                <option key={invoice.id} value={invoice.id}>
                  {invoice.invoice_no} · {fmt(
                    "amount_due" in invoice
                      ? (invoice as APInvoice).amount_due
                      : Number((invoice as ARInvoice).total) - Number((invoice as ARInvoice).received_amount),
                    invoice.currency_code,
                  )} outstanding
                </option>
              ))}
            </select>
            {applicableInvoices.length === 0 && (
              <p className="mt-2 text-xs text-amber-700">
                No posted invoices for this party and currency are available. Return after an invoice is posted.
              </p>
            )}
            <label className="mt-3 block text-xs font-medium text-slate-600">Amount to apply</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              max={pmt.unapplied_amount}
              value={applyAmount}
              onChange={(event) => setApplyAmount(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            />
            <label className="mt-3 block text-xs font-medium text-slate-600">Application date</label>
            <input
              type="date"
              value={applyDate}
              onChange={(event) => setApplyDate(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            />
            <label className="mt-3 block text-xs font-medium text-slate-600">Open fiscal period</label>
            <select
              value={applyPeriodId}
              onChange={(event) => setApplyPeriodId(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <option value="">Select a period</option>
              {openPeriods.map((period) => (
                <option key={period.id} value={period.id}>
                  Period {period.period_no}: {period.start_date} - {period.end_date}
                </option>
              ))}
            </select>
            {applyError && <p className="mt-2 text-sm text-red-600">{applyError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setApplyOpen(false)} className="rounded px-3 py-2 text-sm text-slate-600">
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={
                  applying || !applyInvoiceId || !applyPeriodId ||
                  Number(applyAmount) <= 0 || Number(applyAmount) > Number(pmt.unapplied_amount)
                }
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {applying ? "Applying…" : "Apply payment"}
              </button>
            </div>
          </div>
        </div>
      )}
      {voidOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-gray-900">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Void {pmt.payment_no}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              This posts a reversal and restores every affected invoice balance.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-600">Reversal date</label>
            <input
              type="date"
              value={voidDate}
              onChange={(event) => setVoidDate(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            />
            <label className="mt-3 block text-xs font-medium text-slate-600">Open fiscal period</label>
            <select
              value={voidPeriodId}
              onChange={(event) => setVoidPeriodId(event.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
            >
              <option value="">Select a period</option>
              {openPeriods.map((period) => (
                <option key={period.id} value={period.id}>
                  Period {period.period_no}: {period.start_date} - {period.end_date}
                </option>
              ))}
            </select>
            <label className="mt-3 block text-xs font-medium text-slate-600">Reason</label>
            <textarea
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
              placeholder="Required audit reason"
            />
            {voidError && <p className="mt-2 text-sm text-red-600">{voidError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setVoidOpen(false)} className="rounded px-3 py-2 text-sm text-slate-600">
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={voiding || !voidPeriodId || !voidReason.trim()}
                className="rounded bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {voiding ? "Voiding…" : "Post reversal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
